import { useEffect } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { ThemeProvider } from "@/components/layout/ThemeProvider";
import { ToastProvider } from "@/components/ui/toast";
import { AppRouter } from "@/routes/AppRouter";
import { api } from "@/api/axios";
import { useAuthStore } from "@/store/auth.store";
import { ErrorBoundary } from "@/components/shared/ErrorBoundary";

interface RefreshResponse {
  data: {
    accessToken: string;
    user: {
      id: string;
      email: string;
      fullName: string;
      role: "ADMIN" | "EMPLOYEE";
      employeeId: string | null;
    };
  };
}

function SessionBootstrap({ children }: { children: React.ReactNode }) {
  const setSession = useAuthStore((s) => s.setSession);
  const setInitializing = useAuthStore((s) => s.setInitializing);

  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      try {
        const res = await api.post<RefreshResponse>("/auth/refresh");
        if (!cancelled) {
          setSession(res.data.data.user, res.data.data.accessToken);
        }
      } catch {
        // No valid refresh cookie yet (first visit, or Phase 4 not wired up) — that's fine.
      } finally {
        if (!cancelled) setInitializing(false);
      }
    }

    restoreSession();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <>{children}</>;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <SessionBootstrap>
            <ErrorBoundary>
              <AppRouter />
            </ErrorBoundary>
          </SessionBootstrap>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
